import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col, Badge, Button } from "reactstrap";
import Spinners from "../../components/Common/Spinner";
import ResourceErrorState from "../../components/Common/ResourceErrorState";
import { selectCurrentUser } from "../../store/redux/api/auth/authSlice";
import { hasAnyRole, getUserGroups } from "../../utils/roleUtils";

/** Через сколько ждать профиль, прежде чем признать загрузку неудачной. */
const PROFILE_TIMEOUT_MS = 10000;

/** Бейджи со списком ролей; если ролей нет — приглушённый текст. */
const RoleBadges = ({ roles, emptyText }) =>
  roles.length ? (
    <span className="d-inline-flex flex-wrap gap-1">
      {roles.map((role) => (
        <Badge key={role} color="light" className="text-body border">
          {role}
        </Badge>
      ))}
    </span>
  ) : (
    <span className="text-muted">{emptyText}</span>
  );

/**
 * Компонент-обёртка для защиты маршрутов по ролям (EyeHub).
 *
 * Если roles=null — пропускает всех аутентифицированных пользователей.
 * Если roles=[...] — проверяет наличие хотя бы одной роли из списка.
 *
 * Отказ больше НЕ является молчаливым редиректом на дашборд: пользователь
 * видит, какая роль требуется и какие роли есть у него, — иначе «дашборд
 * вместо запрошенной страницы» неотличим от бага навигации.
 *
 * Пока профиль не загружен (после login isAuthenticated=true появляется раньше,
 * чем user) — спиннер; если профиль так и не пришёл, показываем ошибку с
 * кнопкой «Повторить», а не вечную белую страницу.
 *
 * @param {Array<string>|null} roles - Допустимые роли или null (без ограничений).
 * @param {ReactNode} children
 * @param {string} [redirectTo="/dashboard"] - Куда ведёт кнопка возврата.
 */
const RequireRole = ({ roles, children, redirectTo = "/dashboard" }) => {
  const { t } = useTranslation("errors");
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector(selectCurrentUser);
  const userGroups = getUserGroups(user);

  const profilePending = isAuthenticated && user === null;
  const [profileTimedOut, setProfileTimedOut] = useState(false);

  useEffect(() => {
    if (!profilePending) {
      setProfileTimedOut(false);
      return undefined;
    }
    const timer = setTimeout(() => setProfileTimedOut(true), PROFILE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [profilePending]);

  // Ждём загрузку user: после login isAuthenticated=true раньше, чем user.
  if (profilePending) {
    if (!profileTimedOut) return <Spinners />;
    return (
      <Row className="justify-content-center">
        <Col xl={7} lg={9}>
          <ResourceErrorState
            icon="mdi-account-alert-outline"
            tone="warning"
            title={t("profileLoad.title")}
            description={t("profileLoad.description")}
            actions={
              <Button color="primary" onClick={() => window.location.reload()}>
                <i className="mdi mdi-refresh me-1" aria-hidden="true" />
                {t("profileLoad.retry")}
              </Button>
            }
          />
        </Col>
      </Row>
    );
  }

  if (!hasAnyRole(userGroups, roles)) {
    const required = Array.isArray(roles) ? roles : [];
    const mine = userGroups.map((group) => group.name);

    return (
      <Row className="justify-content-center">
        <Col xl={7} lg={9}>
          <ResourceErrorState
            icon="mdi-lock-outline"
            tone="danger"
            title={t("accessDenied.title")}
            description={t("accessDenied.description")}
            actions={
              <>
                <Link to={redirectTo} className="btn btn-primary">
                  {t("accessDenied.goDashboard")}
                </Link>
                <Link to="/support" className="btn btn-outline-secondary">
                  <i className="mdi mdi-lifebuoy me-1" aria-hidden="true" />
                  {t("accessDenied.requestAccess")}
                </Link>
                <Link to="/profile" className="btn btn-link">
                  {t("accessDenied.openProfile")}
                </Link>
              </>
            }
          >
            <div className="text-start mx-auto" style={{ maxWidth: "28rem" }}>
              <div className="mb-2">
                <span className="text-muted me-2">{t("accessDenied.requiredRoles")}:</span>
                <RoleBadges roles={required} emptyText={t("accessDenied.none")} />
              </div>
              <div>
                <span className="text-muted me-2">{t("accessDenied.yourRoles")}:</span>
                <RoleBadges roles={mine} emptyText={t("accessDenied.none")} />
              </div>
            </div>
          </ResourceErrorState>
        </Col>
      </Row>
    );
  }

  return <>{children}</>;
};

export default RequireRole;
